/**
 * Bill calculation engine — the ONLY place totals, taxes and rounding are computed.
 * Runs identically in the browser (live cart totals), on the server (authoritative, from
 * catalog prices) and later in the print agent. All amounts are integer paise; rates are
 * integer basis points (500 = 5%).
 *
 * Order of operations
 *   gross        = (unit price + add-ons) × qty            (in the item's price mode)
 *   line disc    = % of gross or flat (≤ gross)
 *   bill disc    = manual (% / flat) then coupon, allocated across lines pro-rata
 *                  (largest-remainder, so allocations add up exactly)
 *   net          = gross − line disc − bill disc share
 *   inclusive:     taxable = net × 10000 / (10000 + gst + cess);  tax = net − taxable
 *   exclusive:     taxable = net;  gst = taxable × gst%;  cess = taxable × cess%
 *   intra-state:   CGST = ⌊gst / 2⌋, SGST = gst − CGST       inter-state: IGST = gst
 *   charges      = service (% of item taxable) / packaging (per bill or per item), tax on top
 *   round-off    = rounding(subtotal) − subtotal
 */

const BP = 10_000;

const isInt = (n) => Number.isInteger(n);

function assertPaise(n, what) {
  if (!isInt(n) || n < 0) throw new Error(`${what} must be a non-negative integer (paise), got ${n}`);
}

/** Round half away from zero (money rounding). */
export function roundHalfUp(x) {
  return x < 0 ? -Math.round(-x) : Math.round(x);
}

export function percentOf(amountPaise, rateBp) {
  return roundHalfUp((amountPaise * rateBp) / BP);
}

/** Amount of a discount spec against a base. spec: { type:'percent', valueBp } | { type:'flat', valuePaise } */
export function discountAmount(spec, basePaise) {
  if (!spec || basePaise <= 0) return 0;
  if (spec.type === 'percent') return Math.min(basePaise, percentOf(basePaise, Math.max(0, spec.valueBp ?? 0)));
  if (spec.type === 'flat') return Math.min(basePaise, Math.max(0, spec.valuePaise ?? 0));
  return 0;
}

/** Split `total` across `weights` proportionally; integer results that sum exactly to `total`. */
export function allocate(total, weights) {
  const sum = weights.reduce((a, b) => a + b, 0);
  if (total <= 0 || sum <= 0) return weights.map(() => 0);
  const raw = weights.map((w) => (total * w) / sum);
  const out = raw.map(Math.floor);
  let rest = total - out.reduce((a, b) => a + b, 0);
  const order = raw.map((r, i) => [r - Math.floor(r), i]).sort((a, b) => b[0] - a[0] || a[1] - b[1]);
  for (let k = 0; rest > 0; k = (k + 1) % order.length, rest--) out[order[k][1]] += 1;
  return out;
}

export function applyRounding(amountPaise, mode = 'nearest') {
  switch (mode) {
    case 'none':
      return amountPaise;
    case 'up':
      return Math.ceil(amountPaise / 100) * 100;
    case 'down':
      return Math.floor(amountPaise / 100) * 100;
    case 'nearest':
    default:
      return roundHalfUp(amountPaise / 100) * 100;
  }
}

/** Tax on a net amount. Returns { taxablePaise, gstPaise, cessPaise, cgstPaise, sgstPaise, igstPaise }. */
export function taxFor(netPaise, { priceMode, gstRateBp = 0, cessRateBp = 0 }, supply = 'intra') {
  let taxable;
  let gst;
  let cess;
  if (priceMode === 'exclusive') {
    taxable = netPaise;
    gst = percentOf(taxable, gstRateBp);
    cess = percentOf(taxable, cessRateBp);
  } else {
    taxable = roundHalfUp((netPaise * BP) / (BP + gstRateBp + cessRateBp));
    const taxTotal = netPaise - taxable;
    cess = Math.min(taxTotal, percentOf(taxable, cessRateBp));
    gst = taxTotal - cess;
  }
  const cgst = supply === 'inter' ? 0 : Math.floor(gst / 2);
  const sgst = supply === 'inter' ? 0 : gst - cgst;
  const igst = supply === 'inter' ? gst : 0;
  return { taxablePaise: taxable, gstPaise: gst, cessPaise: cess, cgstPaise: cgst, sgstPaise: sgst, igstPaise: igst };
}

/**
 * @param {object} input
 * @param {Array}  input.lines   [{ lineId, qty, unitPricePaise, priceMode, gstRateBp, cessRateBp, discount? }]
 *                               unitPricePaise already includes selected add-ons.
 * @param {object} [input.billDiscount]   { type, valueBp | valuePaise }
 * @param {object} [input.coupon]         { type, valueBp | valuePaise, maxDiscountPaise?, minBillPaise? }
 * @param {Array}  [input.charges]        [{ key, label, kind:'percent'|'flat'|'per_item', valueBp|valuePaise, gstRateBp, cessRateBp }]
 * @param {'intra'|'inter'} [input.supply]
 * @param {string} [input.rounding]       nearest | none | up | down
 */
export function computeBill({
  lines = [],
  billDiscount = null,
  coupon = null,
  charges = [],
  supply = 'intra',
  rounding = 'nearest',
}) {
  // 1. gross and line discounts
  const work = lines.map((l) => {
    if (!isInt(l.qty) || l.qty < 1) throw new Error(`Line ${l.lineId}: quantity must be a positive whole number`);
    assertPaise(l.unitPricePaise, `Line ${l.lineId} unit price`);
    const grossPaise = l.unitPricePaise * l.qty;
    const lineDiscountPaise = discountAmount(l.discount, grossPaise);
    return { ...l, grossPaise, lineDiscountPaise, afterLinePaise: grossPaise - lineDiscountPaise };
  });

  // 2. bill-level discounts (manual first, then coupon), allocated pro-rata
  const afterLineTotal = work.reduce((s, l) => s + l.afterLinePaise, 0);
  const manualBillDiscountPaise = discountAmount(billDiscount, afterLineTotal);
  let couponDiscountPaise = 0;
  let couponRejected = null;
  if (coupon) {
    const base = afterLineTotal - manualBillDiscountPaise;
    if (coupon.minBillPaise && afterLineTotal < coupon.minBillPaise) couponRejected = 'MIN_BILL';
    else {
      couponDiscountPaise = discountAmount(coupon, base);
      if (coupon.maxDiscountPaise != null) couponDiscountPaise = Math.min(couponDiscountPaise, coupon.maxDiscountPaise);
    }
  }
  const billDiscountPaise = manualBillDiscountPaise + couponDiscountPaise;
  const shares = allocate(
    billDiscountPaise,
    work.map((l) => l.afterLinePaise),
  );

  // 3. tax per line
  const outLines = work.map((l, i) => {
    const netPaise = l.afterLinePaise - shares[i];
    const tax = taxFor(netPaise, l, supply);
    const totalPaise = tax.taxablePaise + tax.gstPaise + tax.cessPaise;
    return {
      lineId: l.lineId,
      qty: l.qty,
      unitPricePaise: l.unitPricePaise,
      priceMode: l.priceMode,
      gstRateBp: l.gstRateBp ?? 0,
      cessRateBp: l.cessRateBp ?? 0,
      amounts: {
        grossPaise: l.grossPaise,
        lineDiscountPaise: l.lineDiscountPaise,
        billDiscountPaise: shares[i],
        netPaise,
        ...tax,
        totalPaise,
      },
    };
  });

  // 4. charges (always tax-exclusive, computed on the item taxable value)
  const itemTaxable = outLines.reduce((s, l) => s + l.amounts.taxablePaise, 0);
  const itemQty = outLines.reduce((s, l) => s + l.qty, 0);
  const outCharges = charges
    .map((c) => {
      let amountPaise;
      if (c.kind === 'percent') amountPaise = percentOf(itemTaxable, c.valueBp ?? 0);
      else if (c.kind === 'per_item') amountPaise = (c.valuePaise ?? 0) * itemQty;
      else amountPaise = c.valuePaise ?? 0;
      if (outLines.length === 0) amountPaise = 0;
      const tax = taxFor(
        amountPaise,
        { priceMode: 'exclusive', gstRateBp: c.gstRateBp ?? 0, cessRateBp: c.cessRateBp ?? 0 },
        supply,
      );
      return {
        key: c.key,
        label: c.label,
        gstRateBp: c.gstRateBp ?? 0,
        cessRateBp: c.cessRateBp ?? 0,
        amountPaise,
        ...tax,
        totalPaise: tax.taxablePaise + tax.gstPaise + tax.cessPaise,
      };
    })
    .filter((c) => c.amountPaise > 0);

  // 5. totals, tax breakup, round-off
  const sum = (arr, k) => arr.reduce((s, x) => s + x[k], 0);
  const la = outLines.map((l) => l.amounts);
  const parts = [...la, ...outCharges];
  const subtotalPaise = sum(la, 'totalPaise') + sum(outCharges, 'totalPaise');
  const grandTotalPaise = applyRounding(subtotalPaise, rounding);

  const breakup = new Map();
  const addBreakup = (gstRateBp, cessRateBp, a) => {
    const k = `${gstRateBp}:${cessRateBp}`;
    const row = breakup.get(k) ?? {
      gstRateBp,
      cessRateBp,
      taxablePaise: 0,
      cgstPaise: 0,
      sgstPaise: 0,
      igstPaise: 0,
      cessPaise: 0,
    };
    row.taxablePaise += a.taxablePaise;
    row.cgstPaise += a.cgstPaise;
    row.sgstPaise += a.sgstPaise;
    row.igstPaise += a.igstPaise;
    row.cessPaise += a.cessPaise;
    breakup.set(k, row);
  };
  outLines.forEach((l) => addBreakup(l.gstRateBp, l.cessRateBp, l.amounts));
  outCharges.forEach((c) => addBreakup(c.gstRateBp, c.cessRateBp, c));

  const totals = {
    itemCount: itemQty,
    grossPaise: sum(la, 'grossPaise'),
    itemDiscountPaise: sum(la, 'lineDiscountPaise'),
    manualBillDiscountPaise,
    couponDiscountPaise,
    billDiscountPaise,
    discountPaise: sum(la, 'lineDiscountPaise') + billDiscountPaise,
    itemsNetPaise: sum(la, 'netPaise'),
    taxablePaise: sum(parts, 'taxablePaise'),
    cgstPaise: sum(parts, 'cgstPaise'),
    sgstPaise: sum(parts, 'sgstPaise'),
    igstPaise: sum(parts, 'igstPaise'),
    cessPaise: sum(parts, 'cessPaise'),
    taxPaise: sum(parts, 'gstPaise') + sum(parts, 'cessPaise'),
    chargesPaise: sum(outCharges, 'totalPaise'),
    subtotalPaise,
    roundOffPaise: grandTotalPaise - subtotalPaise,
    grandTotalPaise,
  };

  return {
    lines: outLines,
    charges: outCharges,
    totals,
    taxBreakup: [...breakup.values()].sort((a, b) => a.gstRateBp - b.gstRateBp || a.cessRateBp - b.cessRateBp),
    supply,
    rounding,
    couponRejected,
  };
}

/**
 * Validate tendered payments against the grand total.
 * payments: [{ mode, amountPaise, tenderedPaise? }]. Change is only possible on cash.
 * Returns { ok, error?, payments (with changePaise), changePaise, duePaise, paidPaise }.
 */
export function settlePayments(grandTotalPaise, payments = []) {
  if (!payments.length) return { ok: false, error: 'Add a payment' };
  let paid = 0;
  let change = 0;
  let due = 0;
  const out = [];
  for (const p of payments) {
    if (!isInt(p.amountPaise) || p.amountPaise <= 0) return { ok: false, error: 'Payment amounts must be positive' };
    let changePaise = 0;
    if (p.mode === 'cash' && p.tenderedPaise != null) {
      if (!isInt(p.tenderedPaise) || p.tenderedPaise < p.amountPaise)
        return { ok: false, error: 'Cash received is less than the amount' };
      changePaise = p.tenderedPaise - p.amountPaise;
    }
    if (p.mode === 'due') due += p.amountPaise;
    paid += p.amountPaise;
    change += changePaise;
    out.push({ ...p, changePaise });
  }
  if (paid !== grandTotalPaise) {
    return {
      ok: false,
      error: paid < grandTotalPaise ? 'Payments are less than the bill total' : 'Payments exceed the bill total',
    };
  }
  return { ok: true, payments: out, changePaise: change, duePaise: due, paidPaise: paid - due };
}

/** Paise → "1,234.50" (Indian grouping). */
export function formatAmount(paise, { symbol = '' } = {}) {
  const sign = paise < 0 ? '-' : '';
  const abs = Math.abs(paise);
  const rupees = Math.floor(abs / 100);
  const p = String(abs % 100).padStart(2, '0');
  const s = String(rupees);
  const last3 = s.slice(-3);
  const rest = s.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ',');
  return `${sign}${symbol}${rest ? `${rest},` : ''}${last3}.${p}`;
}

export function rateLabel(bp) {
  const pct = bp / 100;
  return `${Number.isInteger(pct) ? pct : pct.toFixed(2).replace(/0+$/, '')}%`;
}
